import { CURRENT_SEASON_KEY } from '../seasons';
import { getActiveOrUpcomingTournament } from '../tournaments';

export type HomeCardId =
    | 'schedule_conflicts'
    | 'next_duty_urgent'
    | 'birthday'
    | 'tournament_immediate'
    | 'next_event'
    | 'next_game'
    | 'current_standing'
    | 'tournament_upcoming'
    | 'last_week_matchups'
    | 'upcoming_matchups'
    | 'next_duty_non_urgent';

export interface CachedHomeLayout {
    desktopLeft: HomeCardId[];
    desktopRight: HomeCardId[];
    mobile: HomeCardId[];
    seasonKey?: string;
    timestamp?: number;
}

export const STORAGE_KEY_HOME_LAYOUT = 'chicagojets_home_layout_cache_v3';

export function getDefaultHomeLayout(seasonKey: string = CURRENT_SEASON_KEY, date: Date = new Date()): CachedHomeLayout {
    const activeOrUpcoming = getActiveOrUpcomingTournament(seasonKey, date);
    const isOngoing = activeOrUpcoming?.isOngoing ?? false;
    const hasTournament = !!activeOrUpcoming;

    if (isOngoing) {
        return {
            desktopLeft: ['tournament_immediate', 'current_standing', 'last_week_matchups'],
            desktopRight: ['next_event', 'next_game', 'upcoming_matchups'],
            mobile: ['tournament_immediate', 'next_event', 'next_game', 'last_week_matchups', 'upcoming_matchups', 'current_standing'],
            seasonKey,
            timestamp: Date.now()
        };
    }

    if (hasTournament) {
        return {
            desktopLeft: ['next_event', 'current_standing', 'last_week_matchups'],
            desktopRight: ['next_game', 'tournament_upcoming', 'upcoming_matchups'],
            mobile: ['next_event', 'next_game', 'tournament_upcoming', 'last_week_matchups', 'upcoming_matchups', 'current_standing'],
            seasonKey,
            timestamp: Date.now()
        };
    }

    return {
        desktopLeft: ['next_event', 'current_standing', 'last_week_matchups'],
        desktopRight: ['next_game', 'upcoming_matchups'],
        mobile: ['next_event', 'next_game', 'last_week_matchups', 'upcoming_matchups', 'current_standing'],
        seasonKey,
        timestamp: Date.now()
    };
}

export function getCachedHomeLayout(seasonKey: string = CURRENT_SEASON_KEY): CachedHomeLayout | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_HOME_LAYOUT);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (
                parsed &&
                typeof parsed === 'object' &&
                Array.isArray(parsed.desktopLeft) &&
                Array.isArray(parsed.desktopRight) &&
                Array.isArray(parsed.mobile)
            ) {
                if (!parsed.seasonKey || parsed.seasonKey === seasonKey) {
                    return parsed as CachedHomeLayout;
                }
            }
        }
    } catch (e) {
        console.warn('Failed to parse cached home layout:', e);
    }
    return null;
}

export function setCachedHomeLayout(layout: CachedHomeLayout): void {
    try {
        localStorage.setItem(STORAGE_KEY_HOME_LAYOUT, JSON.stringify(layout));
    } catch (e) {
        console.warn('Failed to cache home layout:', e);
    }
}
